import { useState, useEffect, useMemo } from 'react';
import api from '../../utils/api';
import { PERMISSION_MODULE_GROUPS } from '../../utils/permissions';

/**
 * Mobile App Menu picker.
 *
 * Admin selects which of the existing permission-modules should appear on
 * the mobile (Android/iOS) app home grid. The picker is order-aware: the
 * order in which the admin ticks the boxes is the order they'll appear on
 * the mobile home. A user's regular permission matrix still applies on top —
 * the curated list is intersected with `<module>.view` at fetch-time
 * (see /companies/mobile-menu).
 *
 * Stored as `company.mobile_menu_keys: string[]`. Empty / null = "use
 * sensible field-staff defaults".
 */
const MobileAppMenuPanel = ({ company, onSaved }) => {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [serverDefault, setServerDefault] = useState(false);

  // Flatten all module options. Keep them grouped for the UI but track
  // selections in a single list because the order matters (= home-grid order).
  const ALL_OPTIONS = useMemo(
    () => PERMISSION_MODULE_GROUPS.flatMap((g) =>
      g.modules.map((m) => ({ ...m, group: g.label })),
    ),
    [],
  );

  // Hydrate from the company prop / fetch fallback to ensure we show
  // exactly what the API returned (including defaults).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.resolve();
      if (cancelled) return;
      const fromProp = (company && Array.isArray(company.mobile_menu_keys))
        ? company.mobile_menu_keys
        : null;
      if (fromProp && fromProp.length) {
        setSelected(fromProp);
        setServerDefault(false);
      } else {
        try {
          const res = await api.get('/companies/mobile-menu');
          if (cancelled) return;
          setSelected(res.data?.curated || []);
          setServerDefault(!!res.data?.is_using_defaults);
        } catch {
          if (!cancelled) setSelected([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [company?.id, company]);

  const toggle = (key) => {
    setSelected((s) => (s.includes(key) ? s.filter((x) => x !== key) : [...s, key]));
  };

  const move = (idx, dir) => {
    setSelected((s) => {
      const next = [...s];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return s;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  };

  const save = async () => {
    if (!company?.id) return;
    setSaving(true);
    try {
      await api.put(`/companies/${company.id}`, { mobile_menu_keys: selected });
      setServerDefault(false);
      if (onSaved) onSaved(selected);
      alert('Mobile app menu saved. Field-staff will see this list next time they open the app.');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save mobile menu');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!company?.id) return;
    if (!window.confirm('Clear the custom mobile menu and revert to the system defaults (Attendance, Leads, Activities, Customers, Sales Invoices, Daily Tasks)?')) return;
    setSaving(true);
    try {
      await api.put(`/companies/${company.id}`, { mobile_menu_keys: [] });
      const res = await api.get('/companies/mobile-menu');
      setSelected(res.data?.curated || []);
      setServerDefault(true);
      if (onSaved) onSaved([]);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to reset mobile menu');
    } finally {
      setSaving(false);
    }
  };

  if (!company) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-sm text-gray-500">
        Pick a company above to configure its mobile app menu.
      </div>
    );
  }

  const selectedSet = new Set(selected);
  const lookup = Object.fromEntries(ALL_OPTIONS.map((o) => [o.key, o]));

  return (
    <div className="bg-white rounded-lg shadow" data-testid="mobile-menu-panel">
      <div className="bg-gradient-to-r from-indigo-700 to-purple-700 px-6 py-4 rounded-t-lg">
        <h2 className="text-xl font-bold text-white">📱 Mobile App Menu — {company.name}</h2>
        <p className="text-xs text-white/80 mt-1">
          Choose which modules appear on the Android/iOS home grid. A user&apos;s permission matrix still applies, so an unselected module here means it&apos;s hidden from the mobile app even if the user has access to it on the web.
        </p>
      </div>

      <div className="p-6 grid md:grid-cols-2 gap-6">
        {/* LEFT: Selected (ordered) — drag-up/down via arrow buttons */}
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-700">Selected modules ({selected.length})</h3>
            {serverDefault && <span className="text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">Showing system defaults</span>}
          </div>
          <p className="text-[11px] text-slate-500 mb-2">Use ▲ / ▼ to reorder. Order = home-grid order.</p>
          {selected.length === 0 && (
            <div className="border border-dashed border-slate-300 rounded p-4 text-center text-xs text-slate-500">
              No modules selected — mobile users will see an empty home screen.
            </div>
          )}
          <ul className="divide-y border rounded" data-testid="mobile-menu-selected">
            {selected.map((key, idx) => {
              const m = lookup[key] || { icon: '◻', label: key, group: '?' };
              return (
                <li key={key} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50" data-testid={`mobile-menu-row-${key}`}>
                  <span className="text-base">{m.icon}</span>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{m.label}</div>
                    <div className="text-[10px] text-slate-500">{m.group} · <code>{key}</code></div>
                  </div>
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="px-2 py-1 text-xs border rounded hover:bg-slate-100 disabled:opacity-30" data-testid={`mm-up-${key}`}>▲</button>
                  <button onClick={() => move(idx, +1)} disabled={idx === selected.length - 1} className="px-2 py-1 text-xs border rounded hover:bg-slate-100 disabled:opacity-30" data-testid={`mm-down-${key}`}>▼</button>
                  <button onClick={() => toggle(key)} className="px-2 py-1 text-xs text-rose-600 border border-rose-200 rounded hover:bg-rose-50" data-testid={`mm-remove-${key}`}>✕</button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* RIGHT: Available (grouped) — toggleable checkboxes */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Available modules</h3>
          <p className="text-[11px] text-slate-500 mb-2">Click to add to the mobile menu.</p>
          <div className="space-y-4 max-h-[480px] overflow-y-auto pr-1">
            {PERMISSION_MODULE_GROUPS.map((g) => (
              <div key={g.label}>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">{g.label}</div>
                <div className="grid grid-cols-1 gap-1">
                  {g.modules.map((m) => {
                    const isOn = selectedSet.has(m.key);
                    return (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => toggle(m.key)}
                        className={`flex items-center gap-2 text-left px-3 py-1.5 rounded border text-sm ${isOn ? 'bg-indigo-50 border-indigo-300 text-indigo-800' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
                        data-testid={`mm-toggle-${m.key}`}
                      >
                        <input type="checkbox" checked={isOn} readOnly className="pointer-events-none" />
                        <span>{m.icon}</span>
                        <span className="flex-1">{m.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="px-6 pb-6 flex flex-wrap justify-between items-center gap-3">
        <button
          type="button"
          onClick={resetToDefaults}
          disabled={saving}
          className="text-sm text-slate-600 underline hover:text-slate-800"
          data-testid="mm-reset-defaults"
        >
          Reset to system defaults
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white px-6 py-2 rounded-lg hover:opacity-90 disabled:opacity-50"
          data-testid="mm-save-btn"
        >
          {saving ? 'Saving…' : '💾 Save Mobile Menu'}
        </button>
      </div>
    </div>
  );
};

export default MobileAppMenuPanel;
